import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import popupImage from "@/assets/popup-hair.jpg";

const POPUP_DELAY_MS = 8000;
const DISMISSED_KEY = "plym_popup_dismissed";

const WELCOME_CODE = "WELCOME15";

const EmailSignupPopup = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showCode, setShowCode] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem(DISMISSED_KEY);
    if (dismissed) return;

    const timer = setTimeout(() => setOpen(true), POPUP_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    setOpen(false);
    sessionStorage.setItem(DISMISSED_KEY, "1");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.from("subscribers" as any).insert({
        email,
        marketing_consent: marketingConsent,
      });
      if (error) {
        if (error.code === "23505") {
          toast.info("You're already subscribed!");
        } else {
          throw error;
        }
      } else {
        toast.success("Thanks for signing up! Your discount code is ready.");
      }

      // Fire Zapier webhook if configured. The edge function looks up the
      // webhook URL server-side so it never leaks to public clients.
      try {
        await supabase.functions.invoke("zapier-subscriber-webhook", {
          body: { email, marketingConsent },
        });
      } catch (zapErr) {
        console.warn("Zapier webhook failed (non-blocking):", zapErr);
      }
      localStorage.setItem("plym_welcome_code", WELCOME_CODE);
      setShowCode(true);
      return; // Don't dismiss - show code
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-foreground/50 backdrop-blur-sm animate-in fade-in duration-300"
        onClick={dismiss}
      />

      {/* Modal */}
      <div className="relative z-10 bg-background rounded-lg shadow-2xl overflow-hidden max-w-[720px] w-full grid grid-cols-1 md:grid-cols-2 animate-in fade-in zoom-in-95 slide-in-from-bottom-4 duration-400">
        {/* Image side */}
        <div className="hidden md:block relative">
          <img
            src={popupImage}
            alt="Beautiful hair"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Form side */}
        <div className="relative p-8 md:p-10 flex flex-col justify-center bg-[hsl(var(--muted)/0.3)]">
          <button
            onClick={dismiss}
            className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          {showCode ? (
            <>
              <p className="font-display text-sm uppercase tracking-[0.2em] text-primary mb-3">
                You're in! Use this code at checkout:
              </p>
              <div className="mb-4 bg-background border-2 border-dashed border-primary rounded-lg p-4 text-center">
                <span className="font-display text-3xl md:text-4xl font-bold text-primary tracking-wider">
                  {WELCOME_CODE}
                </span>
              </div>
              <p className="font-display text-lg font-semibold uppercase tracking-[0.15em] text-foreground mb-4">
                15% Off Your First Order
              </p>
              <a
                href="/shop"
                onClick={dismiss}
                className="w-full bg-foreground text-background font-body font-semibold text-sm uppercase tracking-[0.15em] py-3.5 rounded hover:bg-foreground/90 transition-colors text-center block"
              >
                Start Shopping
              </a>
            </>
          ) : (
            <>
              <p className="font-display text-sm uppercase tracking-[0.2em] text-primary mb-3">
                Sign up to our mailing list & treat yourself to
              </p>

              <div className="mb-1">
                <span className="font-display text-6xl md:text-7xl font-bold text-foreground leading-none">
                  15%
                </span>
              </div>
              <p className="font-display text-lg font-semibold uppercase tracking-[0.15em] text-foreground mb-6">
                Off Your First Order
              </p>

              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="YOUR BEST EMAIL"
                  className="w-full px-4 py-3 rounded border border-border bg-background text-sm font-body placeholder:text-muted-foreground/60 placeholder:uppercase placeholder:tracking-wider focus:outline-none focus:ring-2 focus:ring-primary/30"
                />

                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-border accent-primary"
                  />
                  <span className="text-xs font-body text-muted-foreground leading-relaxed">
                    Check this box if you'd also like to receive promotional marketing messages (Exclusive deals, offers, and coupons).
                  </span>
                </label>

                <button
                  type="submit"
                  className="w-full bg-foreground text-background font-body font-semibold text-sm uppercase tracking-[0.15em] py-3.5 rounded hover:bg-foreground/90 transition-colors"
                >
                  Sign Up
                </button>
              </form>

              <p className="text-[10px] font-body text-muted-foreground/70 leading-relaxed mt-4">
                By submitting this form, you consent to receive informational and/or marketing messages from PlymHair. Consent is not a condition of purchase. Unsubscribe at any time.{" "}
                <a href="/privacy" className="underline">Privacy Policy</a> &{" "}
                <a href="/terms" className="underline">Terms</a>.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailSignupPopup;